import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';

import { Fields, Field } from 'redux-form';

import Badge from 'components/common/Badge';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import OrderInformationSectionTitle from 'components/workflow/OrderInformation/OrderInformationSectionTitle';
import TextField from 'components/fields/TextField';
import DatepickerField from 'components/fields/DatepickerField';

import pxToEm from 'styles/pxToEm';
import pxToRem from 'styles/pxToRem';

import sortInsurance from 'utils/sortInsurance';
import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';

const styles = theme => ({
  label: {
    display: 'inline-block',
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
    whiteSpace: 'nowrap',
  },
  typography: {
    padding: '10px 0 11px 0',
    lineHeight: pxToEm(16, 14),
  },
});

const OrderInformationGeneralInsuranceFields = ({
  insuranceInformation, systemSettings, classes, disabled, ...fields
}) => {
  const insurancesField = get(fields, 'Insurances');
  const selfPayer = insuranceInformation.find(item => item['Insurance Type'] === 'Self')
    || {
      ID: null,
      'Order Benefits': null,
      'Insurance Info': {
        'Payer Name': 'Self Payer',
      },
      'Insurance Type': 'Self',
    };
  selfPayer['Insurance Info']['Payer Name'] = 'Self Payer';
  const allPayers = [
    ...sortInsurance(insuranceInformation)
      .filter(item => item['Insurance Status'] === 'Active'
      && (!item['Insurance Type'] || item['Insurance Type'] !== 'Self')),
    selfPayer,
  ];
  const payers = insurancesField.input.value
    ? allPayers.filter(item =>
      !insurancesField.input.value.find(insurancesItem => insurancesItem.value === item.ID))
    : allPayers;
  return (
    <Fragment>
      <Grid
        item
        xs={12}
      >
        <OrderInformationSectionTitle title="Payer Name (Insurance / Credit Card)" />
        <Fields
          id="Insurances"
          names={[
            'Insurance Name',
            'Insurances',
          ]}
          autosuggestFieldName="Insurance Name"
          fieldArrayName="Insurances"
          placeholder="-Select Payer-"
          errorPlacement="top"
          component={MultiselectAutosuggestField}
          validate={[validate.required]}
          renderSuggestionContent={suggestion => (
            <div>
              {suggestion['Order Benefits'] &&
                <Badge color="primary" gutter="right">
                  {suggestion['Order Benefits'] === 'Primary' && '1'}
                  {suggestion['Order Benefits'] === 'Secondary' && '2'}
                  {suggestion['Order Benefits'] === 'Secondary As Primary' && '2'}
                  {suggestion['Order Benefits'] === 'Tertiary' && '3'}
                </Badge>
              }
              <span>{suggestion['Insurance Info']['Payer Name']}</span>
            </div>
          )}
          renderInputContent={suggestion => suggestion['Insurance Info']['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            if (insurancesField.input.value.indexOf(suggestion.ID) === -1) {
              insurancesField.input.onChange([
                ...insurancesField.input.value || [],
                {
                  value: suggestion.ID,
                  label: suggestion['Insurance Info']['Payer Name'],
                  ...suggestion['Insurance Type'] === 'Self' ? { type: 'Self' } : { type: 'Normal' },
                  ...suggestion['Order Benefits'] === 'Primary' ? { startBadge: '1' } : {},
                  ...suggestion['Order Benefits'] === 'Secondary' ? { startBadge: '2' } : {},
                  ...suggestion['Order Benefits'] === 'Secondary As Primary' ? { startBadge: '2' } : {},
                  ...suggestion['Order Benefits'] === 'Tertiary' ? { startBadge: '3' } : {},
                  profitPercentMedicaid: suggestion['Insurance Info']['Profit Percent Medicaid'],
                  profitPercentMedicare: suggestion['Insurance Info']['Profit Percent Medicare'],
                  profitPercentDefault: systemSettings['System Config'].PROFIT_PERCENT,
                },
              ]);
            }
            return '';
          }}
          fetchSuggestions={({ value }) => (new Promise((resolve) => {
            if (value.length > 0) {
              resolve(payers
                .filter(item => item['Insurance Info']['Payer Name'].toLowerCase().indexOf(value.toLowerCase()) !== -1));
            } else {
              resolve(payers);
            }
          }))}
          firstHighlighted
          draggable
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <OrderInformationSectionTitle title="Authorization" />
        <Grid
          container
          justify="flex-start"
          spacing={16}
        >
          <Grid item xs={12} lg={8}>
            <Fields
              id="Authorization From / To"
              names={[
                'Authorization From',
                'Authorization To',
              ]}
              component={DatepickerField}
              validate={[validate.date]}
              normalize={{
                'Authorization From': normalize.date,
                'Authorization To': normalize.date,
              }}
              disabled={(insurancesField.input.value[0]
                && insurancesField.input.value[0].type === 'Self') || disabled}
              fullWidth
              minDate="01/01/1900"
              maxDate="01/01/2100"
              dateFormat={dateFormat}
              placeholder="mm/dd/yyyy"
              variant="range"
              startDateName="Authorization From"
              endDateName="Authorization To"
              alwaysPrefillEndDate
              inputAdornmentLgOnly
              rangeButtons
              datepickerPlacement="middle-right"
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Field
              id="Authorization"
              name="Authorization"
              component={TextField}
              placeholder="-Auth-"
              validate={[validate.minLength4]}
              disabled={(insurancesField.input.value[0]
                && insurancesField.input.value[0].type === 'Self') || disabled}
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

OrderInformationGeneralInsuranceFields.propTypes = {
  insuranceInformation: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    label: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
  }).isRequired,
};

OrderInformationGeneralInsuranceFields.defaultProps = {
  insuranceInformation: [],
  systemSettings: {},
  disabled: false,
};

export default withStyles(styles)(OrderInformationGeneralInsuranceFields);



// WEBPACK FOOTER //
// ./src/components/workflow/OrderInformation/OrderInformationGeneral/OrderInformationGeneralInsuranceFields.js